import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAdvancedNoticeComponentFilters } from '../filters/advancedFiltersSelectors';
import { NoticeComponent } from '@/types';
import { useDebouncedState } from '../helpers/hooks';
import {
  useGetNoticeComponentsQuery,
  useLazyGetNoticeComponentsQuery,
} from '../services/edgeApi';

export const useNoticeComponents = ({ pageSize = 30 } = {}) => {
  const {
    tsps,
    textSearch,
    hasPoints,
    hasNoticeActivity,
    hasCapacityComponent,
    mapped,
    directions,
    capacityTypes,
  } = useSelector(selectAdvancedNoticeComponentFilters);

  const [shouldFetch, setShouldFetch] = useState<boolean>(true);
  const [exhausted, setExhausted] = useState<boolean>(false);
  const [cursor, setCursor] = useState<number>(0);
  const [noticeComponents, setNoticeComponents] = useState<
    Record<string, NoticeComponent>
  >({});

  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(
    textSearch,
    500,
  );

  useEffect(() => {
    setDebouncedSearch(textSearch);
  }, [textSearch]);

  useEffect(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setNoticeComponents({});
  }, [
    debouncedSearch,
    tsps,
    hasPoints,
    hasNoticeActivity,
    hasCapacityComponent,
    mapped,
    directions,
    capacityTypes,
  ]);

  const { data, isLoading, isFetching, isError } = useGetNoticeComponentsQuery(
    {
      limit: pageSize,
      offset: cursor,
      hasPoints,
      hasNoticeActivity,
      hasCapacityComponent,
      mapped,
      tsps,
      directions,
      capacityTypes,
      textSearch: debouncedSearch,
    },
    {
      skip: !shouldFetch || exhausted,
    },
  );

  const [getNoticeComponentsTrigger] = useLazyGetNoticeComponentsQuery();

  if (shouldFetch && isFetching === false && exhausted === false) {
    setNoticeComponents((prevComponents) => ({ ...prevComponents, ...data }));
    setShouldFetch(false);
    if (data && Object.values(data).length > 0) {
      setCursor((prevCursor) => prevCursor + Object.values(data).length);
    }
    if (data && Object.values(data).length < pageSize) {
      setExhausted(true);
    }
  }

  const next = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  const reset = useCallback(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setNoticeComponents({});
  }, [setShouldFetch, setCursor, setExhausted, setNoticeComponents]);

  const refresh = useCallback(() => {
    // Add a slight delay to the refresh
    setTimeout(async () => {
      try {
        const result = await getNoticeComponentsTrigger({
          limit: cursor,
          offset: 0,
          hasPoints,
          hasNoticeActivity,
          hasCapacityComponent,
          mapped,
          tsps,
          directions,
          capacityTypes,
          textSearch: debouncedSearch,
        }).unwrap();
        if (result) {
          setNoticeComponents({ ...result });
        }
      } catch (error) {
        console.error('Error refreshing notice components:', error);
      }
    }, 300);
  }, [
    getNoticeComponentsTrigger,
    cursor,
    hasPoints,
    hasNoticeActivity,
    hasCapacityComponent,
    mapped,
    tsps,
    debouncedSearch,
    directions,
    capacityTypes,
  ]);

  return {
    noticeComponents,
    isLoading,
    isFetching,
    isError,
    exhausted,
    next,
    reset,
    refresh,
  };
};
